import { useContext, useRef, useEffect, useState } from 'react';
import { AppRelevantDataContext } from '../../AppContext';
import { GroupedVirtuoso } from 'react-virtuoso';
import { Menu } from 'antd';
import { TbDeviceDesktop } from 'react-icons/tb';
import { useDispatch, useSelector } from 'react-redux';
import { PROD_DEVC_OWNER_USER_ID } from '../../VcConstants';
import { setReRenderTreeUponSearch } from './Store/Slices/deviceList';
import { updateIsAdvSearchedFilterDataEmpty } from './Store/Slices/advanceSearchSlice';

const VcVirtualizedDeviceTree = ({ state, selectedNodeID, renderDotBasedOnDeviceActiveStatus, renderSelectedSingleDeviceData, renderDropDownMenu, setToiletId }) => {

    const context = useContext(AppRelevantDataContext);
    const virtuosoRef = useRef(null); 
    const dispatch = useDispatch();
    const { openToiletNodesID } = useSelector((state) => state.treeAndSearchSlice);
    const reRenderTreeUponSearch = useSelector(state => state.deviceList.reRenderTreeUponSearch);
    const advanceSearchState = useSelector((state) => state?.advSearchList);

    const [ virtualised, virtualisedSetState ] = useState({
        groupedDeviceList : [],
        groupCounts : [],
        groupDevices : [],
        independentDevices : [],
        trackDeviceConditionalRender : [],
        combinedGroupCounts : [],
        arrayGroup : [],
        allDevices : [],
        totalGroups: 0,
        deviceIndexMap: new Map(),
    });

    const contains = (val, search) => {
        if (!search) return true;
        if (!val) return false;
        return String(val).toLowerCase().includes(search.toLowerCase());
    };
    
    // Called only once since when <VcDeviceTree /> component is mounted
    // Below useEffect does all the calculations and sets the state for virtualised device tree
    useEffect(() => {
        if(window.location.pathname === "/device/addNode") {
            scrollToSearchedElement();
        }

        var groupedDeviceList = [];
        var independentDevices = [];

        if(advanceSearchState.advSearchFlag == true) {

            if(advanceSearchState.toiletName) {
                groupedDeviceList = (state.toiletNodeInfo || []).map(toilet => {
                    const toiletMatch =
                        contains(toilet.title, advanceSearchState.toiletName) ||
                        contains(toilet.objNodeInfo?.DisplayName, advanceSearchState.toiletName);

                        if (toiletMatch && toilet?.hasDevices) {
                            const devices = state.toiletDevices.filter(device => device.TreeParentID === toilet.id);
                            return {
                                groupTitle: toilet.title,
                                groupId: toilet.id,
                                nodePath: toilet.path,
                                nodeDevices: devices
                            };
                        }
                    return null;
                }).filter(Boolean);
            }

            if(advanceSearchState.hierarchyNode || advanceSearchState.hierarchyNode.length > 0) {

                groupedDeviceList = (state.toiletNodeInfo || []).map(toilet => {
                    const HierarchyMatch = contains(toilet.path, advanceSearchState.hierarchyNode);
                        if (HierarchyMatch && toilet?.hasDevices) {
                            const devices = state.toiletDevices.filter(device => device.TreeParentID === toilet.id);
                            return {
                                groupTitle: toilet.title,
                                groupId: toilet.id,
                                nodePath: toilet.path,
                                nodeDevices: devices
                            };
                        }
                    return null;
                }).filter(Boolean);
            }

            if(advanceSearchState.deviceId){

                // Filter toilet nodes that have devices matching the device search criteria
                groupedDeviceList = (state.toiletNodeInfo || []).map(toilet => {
                    if (toilet?.hasDevices) {
                        const devices = state.toiletDevices.filter(device => {

                            const deviceIdMatch = advanceSearchState.deviceId ? contains(device.DeviceID, advanceSearchState.deviceId) : true;
                            return deviceIdMatch && device.TreeParentID === toilet.id;
                        });
                        if (devices.length > 0) {
                            return {
                                groupTitle: toilet.title,
                                groupId: toilet.id,
                                nodePath: toilet.path,
                                nodeDevices: devices
                            };
                        }
                    }
                    return null;
                }).filter(Boolean);

                independentDevices = state.independentDevices.filter(device => {
                    const deviceIdMatch = !advanceSearchState.deviceId ||
                        contains(device.DeviceID, advanceSearchState.deviceId);

                        if(deviceIdMatch){  
                            return device;  
                        }
                }).filter(Boolean);
                
            }   

              if(advanceSearchState.deviceName){

                // Filter toilet nodes that have devices matching the device search criteria
                groupedDeviceList = (state.toiletNodeInfo || []).map(toilet => {
                    if (toilet?.hasDevices) {
                        const devices = state.toiletDevices.filter(device => {

                            const deviceNameMatch = advanceSearchState.deviceName ? contains(device.DeviceName, advanceSearchState.deviceName) : true;
                            return deviceNameMatch && device.TreeParentID === toilet.id;
                        });
                        if (devices.length > 0) {
                            return {
                                groupTitle: toilet.title,
                                groupId: toilet.id,
                                nodePath: toilet.path,
                                nodeDevices: devices
                            };
                        }
                    }
                    return null;
                }).filter(Boolean);

                independentDevices = state.independentDevices.filter(device => {
                    const deviceNameMatch = !advanceSearchState.deviceName ||
                        contains(device.DeviceName, advanceSearchState.deviceName);

                    if(deviceNameMatch){
                        return device;
                    }
                    
                });                
            }  

            const noResultsFound = groupedDeviceList.length === 0 && independentDevices.length === 0;

            if (noResultsFound) {

                dispatch(updateIsAdvSearchedFilterDataEmpty(true));

                //  ALWAYS show full list when no results
                groupedDeviceList = (state.toiletNodeInfo || [])
                    .map(toilet => {
                    if (toilet?.hasDevices) {
                        const devices = state.toiletDevices.filter(
                        device => device.TreeParentID === toilet.id
                        );
                        return {
                        groupTitle: toilet.title,
                        groupId: toilet.id,
                        nodePath: toilet.path,
                        nodeDevices: devices
                        };
                    }
                    return null;
                    })
                    .filter(Boolean);

                independentDevices = state.independentDevices || [];

            } else {
                dispatch(updateIsAdvSearchedFilterDataEmpty(false));
            }

        } else {

            // Creates a grouped device list based on toilet nodes and their devices
            groupedDeviceList = (state.toiletNodeInfo || []).map(toilet => {
                if (toilet?.hasDevices) {
                    const devices = state.toiletDevices.filter(device => device.TreeParentID === toilet.id);
                    return { 
                        groupTitle: toilet.title,
                        groupId: toilet.id,
                        nodePath: toilet.path,
                        nodeDevices: devices
                    };
                }
                
                return null;
            }).filter(Boolean);
            independentDevices = state.independentDevices || [];

        }

        const totalGroups = groupedDeviceList.length;
        const trackDeviceConditionalRender = context.devicesToTrack?.DevicesInfo?.length > 0 && (context.devicesToTrack.ownerOfTrackedDevices?.length <= 0 || context.devicesToTrack.ownerOfTrackedDevices === PROD_DEVC_OWNER_USER_ID);
        const groupCounts = groupedDeviceList.map((group) => group.nodeDevices.length);
        const combinedGroupCounts = trackDeviceConditionalRender ? [context.devicesToTrack.DevicesInfo.length] : [...groupCounts, independentDevices.length];
        const groupDevices = groupedDeviceList.map((group) => group.nodeDevices);
        const arrayGroup = independentDevices && independentDevices.length > 0 ? [...groupDevices, independentDevices] : [...groupDevices];
        const allDevices = trackDeviceConditionalRender ? [context.devicesToTrack.DevicesInfo] : arrayGroup;

        // Map: deviceId → flat index
        const deviceIndexMap = new Map();
        let currentIndex = 0;
        groupedDeviceList.forEach((group) => {
            group.nodeDevices.forEach((device) => {
                deviceIndexMap.set(device.DeviceID, currentIndex + 1);
                currentIndex++;
            });
        });
        
        // Orphan devices (Indepedent Devices)
        independentDevices.forEach(device => {
            deviceIndexMap.set(device.DeviceID, currentIndex++);
        });

        // Setting redux store to auto-scroll to the selected nodeID when page is refreshed, redirected from All Device Dashboard Data Table or Track Device(s)
        dispatch(setReRenderTreeUponSearch(Date.now()));

        // Setting the state with all the computed values
        virtualisedSetState(prev => ({
            ...prev,
            groupedDeviceList: groupedDeviceList,
            groupCounts: groupCounts,
            groupDevices: groupDevices,
            independentDevices: independentDevices,
            trackDeviceConditionalRender: trackDeviceConditionalRender,
            combinedGroupCounts: combinedGroupCounts,
            arrayGroup: arrayGroup,
            allDevices: allDevices,
            totalGroups: totalGroups,
            deviceIndexMap: deviceIndexMap
        }));
    }, [state.toiletNodeInfo, state.toiletDevices, state.independentDevices, state.trackDeviceArr, advanceSearchState])

    // Auto-scroll to the selected nodeID when node searching, page refresh, redirect from All Device Dashboard Data Table or Track Device(s)
    useEffect(() => {
        setTimeout(() => {
            scrollToSearchedElement();        
        }, 200)
    }, [reRenderTreeUponSearch, virtualised])

    // Below function will scroll to a toilet node when searched/clicked
    const scrollToToilet = (toiletId, invokedFrom) => {
        const groupIndex = virtualised.groupedDeviceList.findIndex((t) => t.groupId === toiletId);
        const index = virtualised.groupCounts.slice(0, groupIndex).reduce((a, b) => a + b, 0);

        // To resolve auto-adjustment of nodes at device tree when a toilet node is clicked
        if(invokedFrom && invokedFrom == "treeNodeClicked") {
            const eleBlock = toiletId && document.querySelector(`[nodeId="${toiletId}"]`);
            const blockPos = eleBlock?.getBoundingClientRect()?.top;

            // If an already selected toilet node is clicked again, no scroll will happen
            if(context.selectedNodeInfo.nodeID == toiletId && (blockPos > 200 && blockPos < 550)) {
                return; // to avoid double scroll when the same toilet node is clicked again
            }

            // If an already selected toilet node is clicked again, but that node is on the start and end of the viewport, node will be scrolled to center
            // to resolve auto-adjustment of node when clicked
            if(blockPos < 200 || blockPos > 550) {
                setTimeout(() => {
                    virtuosoRef.current?.scrollToIndex({index: index, align: 'center', behavior: 'smooth'});
                }, 200);
                return; // No further processing required
            }
        } 

        // Normal scroll to toilet node when searched from search bar, page refresh
        setTimeout(() => {
            virtuosoRef.current?.scrollToIndex({index: index, align: 'center', behavior: 'smooth'});
        }, 200);
    };

    // Below function will scroll to a device node when searched
    const scrollToDevice = (deviceId) => {
        const flatIndex = virtualised.deviceIndexMap.get(deviceId);
        flatIndex && virtuosoRef.current?.scrollToIndex({ index: flatIndex, align: 'center', behavior: 'smooth' });
    };

    // This function will call either scrollToToilet or scrollToDevice based on the searched node ID's type in nature (toilet or device)
    const scrollToSearchedElement = () => {
        let nodeId = context.selectedNodeInfo.nodeID;
        if(window.location.pathname === "/device/addNode") {
            nodeId ? scrollToToilet(nodeId) : scrollToDevice(nodeId);    
        } else {
            context.selectedNodeInfo.hasDevc ? scrollToToilet(nodeId) : scrollToDevice(nodeId);
        }
    };

    // This function will return the local index of a device within its group based on the global index
    const getLocalIndex = (globalIndex, nodeIndex) => {
        if(!virtualised.combinedGroupCounts || !virtualised.combinedGroupCounts[nodeIndex]) return 0;
        const totalCount = virtualised.combinedGroupCounts.slice(0, nodeIndex).reduce((prev, next) => prev + next, 0);
        return globalIndex - totalCount;
    };

    return (
        <div
            id="treeViewLayout"
            className='relative rounded'
        >
            <GroupedVirtuoso
                ref={virtuosoRef}
                followOutput="smooth"
                // The below computeItemKey is to ensure that when devices are added/removed, the scroll position is maintained correctly, thus resolving 
                // the first and last node device(s) renderation on node expand
                computeItemKey={(index, groupIndex) => 
                    virtualised.groupedDeviceList[groupIndex]?.nodeDevices[index]?.DeviceID ?? `${groupIndex}-${index}`
                } 
                // data provided inside `groupCounts` prop specifies groupedVirtuoso how many devices are there in each node which needs to be rendered 
                groupCounts={virtualised.allDevices.map(group => group.length)}
                components={{
                    Group: ({ children, ...props }) => (
                    <div {...props} style={{ position: 'relative' }}>
                        {children}
                    </div>
                    ),
                }}
                // groupContent is for rendering the toilet nodes
                // The last group is for independent devices which do not belong to any toilet node
                groupContent={(index) => {
                    const isIndependentList = virtualised.allDevices.length !== virtualised.groupedDeviceList.length && index === virtualised.allDevices.length - 1;
                    let node = virtualised.groupedDeviceList[index]
                    if(isIndependentList) {
                        return <Menu>
                            <Menu.SubMenu style={{ height: "1px", overflow: 'hidden' }} />
                        </Menu>
                    }

                    return (
                        <Menu
                            className='fw-bold rounded myMenu'
                            mode="inline"
                            openKeys={openToiletNodesID ? openToiletNodesID : []}
                            style={{ height: '100%', borderRight: 0, color: '#e6e6e6', backgroundColor: '#fcfeff' }}
                        >
                            <Menu.SubMenu
                                nodeId={node?.groupId}
                                key={node?.groupId}
                                forceSubMenuRender={true} // To ensure submenu is rendered even if not open
                                className='mySelector'
                                title={
                                    <div className="d-flex align-items-center">
                                        <div style={{fontSize: "1.3rem", color: "gray" }}>
                                            <TbDeviceDesktop /> 
                                        </div>
                                        <span style={{marginLeft: "8px"}}>{node?.groupTitle}</span>
                                    </div>
                                }
                                style={{ color: "black",  margin: "3px", backgroundColor: selectedNodeID === node?.groupId ? '#def1ff' : '#fcfeff', height: "40px"}}
                                onTitleClick={() => {
                                    setToiletId(node?.groupId, node?.groupTitle, node?.nodePath)

                                    // Toggle the open/collapse state of the toilet node to show/hide its devices
                                    scrollToToilet(node?.groupId, "treeNodeClicked");
                                }}
                            />
                        </Menu>
                    )
                }}
                // itemContent is for rendering the devices within a toilet node
                // The last group is for independent devices which do not belong to any toilet node
                itemContent={(globalIndex, nodeIndex) => {
                    const isIndependentGroup = nodeIndex === virtualised.totalGroups;

                    if (!isIndependentGroup) {
                        let group = virtualised.groupedDeviceList[nodeIndex];
                        let groupId = group?.groupId;

                        // Skip rendering devices if their toilet node is collapsed inactive 
                        if (!openToiletNodesID.includes(groupId)) {
                           return <div style={{ height: "1px", visibility: 'hidden' }} />;
                        }
                    }

                    const deviceIndex = getLocalIndex(globalIndex, nodeIndex);
                    const device = virtualised.allDevices?.[nodeIndex]?.[deviceIndex];

                    if (!device) return null; // avoid rendering null
                    return (
                        <Menu 
                            className='myMenu' style={{ backgroundColor: "#f5f7f8" }}
                            openKeys={openToiletNodesID ? openToiletNodesID : []}
                        >
                            <div
                                className='d-flex align-items-center deviceBlock'
                                deviceitemid={device?.DeviceID}
                                style={
                                selectedNodeID === device?.DeviceID
                                    ? {
                                        backgroundColor: '#def1ff',
                                        borderBottom: '1px solid lightgray',
                                        borderBottomLeftRadius: '5px',
                                        margin: nodeIndex === virtualised.totalGroups ? '0px' :  '3px 0px 3px 1.6rem',
                                    }
                                    : {
                                        margin: nodeIndex === virtualised.totalGroups ? '3px 0px' :  '3px 0px 3px 1.4rem',
                                        borderBottom: '1px solid lightgray',
                                        borderBottomLeftRadius: '5px',
                                    }
                                }
                            >
                                <div id='deviceStatus'
                                style={{
                                    borderRadius: '50%',
                                    border: '1px solid lightgrey',
                                    backgroundColor: renderDotBasedOnDeviceActiveStatus(device?.DeviceID),
                                    height: '0.5rem',
                                    width: '0.6rem',
                                    marginLeft: '10px',
                                }}
                                ></div>

                                <Menu.Item
                                    className='deviceItem'
                                    id={device?.DeviceID}
                                    style={{ padding: '7px', fontSize: '12px', marginLeft: '7px' }}
                                    onClick={() =>
                                        renderSelectedSingleDeviceData(
                                            device?.DeviceID,
                                            device?.DeviceName,
                                            device?.parentID,
                                            device?.SelectedNodeDeviceType,
                                            device?.nodePath || ''
                                        )
                                    }
                                    key={device?.key}
                                >
                                    <div 
                                        style={{ marginTop: "-2.9rem" }} 
                                        className='deviceTitle' deviceitem={device?.DeviceID}
                                    >
                                        <div
                                            id={device?.DeviceID}
                                            className='d-flex align-items-start fw-bold renderDeviceName'
                                        >
                                        {device?.DeviceName?.length > 27
                                            ? device?.DeviceName.substring(0, 27) + '..'
                                            : device?.DeviceName}
                                        </div>
                                    </div>
                                </Menu.Item>

                                <div>{device && renderDropDownMenu(device)}</div>
                            </div>
                        </Menu>
                    );
                }}
                style={{ overflowAnchor: 'none' }}
            />  
        </div>
    );
};

export default VcVirtualizedDeviceTree;